import React, { useEffect, useState } from 'react';
import { get } from '../utils/api';

const fmt = n => new Intl.NumberFormat('ru-RU').format(Math.round(n || 0)) + ' ₽';

export default function BalancePage() {
  const [data, setData] = useState(null);
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const d = await get(`/reports/balance?as_of=${asOf}`); setData(d); }
    catch (e) {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [asOf]);

  const cashByMethod = {};
  (data?.cash || []).forEach(r => { cashByMethod[r.payment_method] = r.balance || 0; });

  const totalDepositsExpected = (data?.depositsByContract || []).reduce((s, r) => s + r.expected, 0);
  const totalDepositsPaid = (data?.depositsByContract || []).reduce((s, r) => s + r.paid, 0);
  const totalDepositsRemaining = totalDepositsExpected - totalDepositsPaid;
  const depositsReceived = data?.depositsReceived || 0;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Баланс <span style={{ fontSize: 14 }}>упрощённый</span></h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <label style={{ fontSize: 13, color: 'var(--text2)' }}>На дату:</label>
          <input type="date" value={asOf} onChange={e => setAsOf(e.target.value)} style={{ width: 160 }} />
        </div>
      </div>

      {loading && <p style={{ color: 'var(--text3)' }}>Загрузка...</p>}

      {!loading && data && (<>
        {/* Top KPIs */}
        <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(5,1fr)', marginBottom: 24 }}>
          <div className="stat-card">
            <div className="stat-label">Денег на счетах</div>
            <div className={`stat-value ${data.totalCash >= 0 ? 'green' : 'red'}`}>{fmt(data.totalCash)}</div>
          </div>
          <div className="stat-card" style={{ borderColor: 'rgba(239,68,68,.3)' }}>
            <div className="stat-label">Обязательства по залогам</div>
            <div className="stat-value red">{fmt(totalDepositsPaid)}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>должны вернуть арендаторам</div>
          </div>
          <div className="stat-card" style={{ borderColor: 'rgba(251,191,36,.3)' }}>
            <div className="stat-label">Фонд Залоги (факт)</div>
            <div className={`stat-value ${(data.depositFundBalance||0) >= 0 ? 'green' : 'red'}`}>{fmt(data.depositFundBalance||0)}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>реальные деньги в фонде</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Фонд непред. расходов</div>
            <div className={`stat-value ${data.emergencyFund >= 0 ? 'green' : 'red'}`}>{fmt(data.emergencyFund)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Овернайт (накоплено)</div>
            <div className="stat-value blue">{fmt(data.overnightBalance)}</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
          {/* Cash by payment method */}
          <div className="card">
            <div className="section-title">Остатки по счетам</div>
            {data.cash.filter(r => r.payment_method !== 'На карту').length === 0 ? (
              <div style={{ color: 'var(--text3)', fontSize: 13 }}>Нет данных</div>
            ) : (
              data.cash.filter(r => r.payment_method !== 'На карту').map(r => (
                <div key={r.payment_method} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{r.payment_method}</div>
                    <div style={{ fontSize: 12, color: 'var(--text3)' }}>{r.payment_method === 'Наличные' ? 'Касса' : 'Банк'}</div>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 18, color: r.balance >= 0 ? 'var(--green)' : 'var(--red)' }}>
                    {fmt(r.balance)}
                  </div>
                </div>
              ))
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 15, paddingTop: 12, marginTop: 4 }}>
              <span>ИТОГО</span>
              <span style={{ color: data.totalCash >= 0 ? 'var(--green)' : 'var(--red)' }}>{fmt(data.totalCash)}</span>
            </div>
          </div>

          {/* Emergency fund + Overnight */}
          <div className="card">
            <div className="section-title">Резервы и фонды</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
              <div>
                <div style={{ fontWeight: 600 }}>Фонд непредвиденных расходов</div>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>Накопительный учёт</div>
              </div>
              <div style={{ fontWeight: 700, fontSize: 16, color: data.emergencyFund >= 0 ? 'var(--green)' : 'var(--red)' }}>
                {fmt(data.emergencyFund)}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
              <div>
                <div style={{ fontWeight: 600 }}>Овернайт</div>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>Доходы от краткосрочной аренды</div>
              </div>
              <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--accent)' }}>
                {fmt(data.overnightBalance)}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
              <div>
                <div style={{ fontWeight: 600 }}>Фонд Залоги (факт. деньги)</div>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>Реальный остаток в фонде</div>
              </div>
              <div style={{ fontWeight: 700, fontSize: 16, color: (data.depositFundBalance||0) >= 0 ? 'var(--green)' : 'var(--red)' }}>
                {fmt(data.depositFundBalance||0)}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0' }}>
              <div>
                <div style={{ fontWeight: 600 }}>Обязательства по залогам</div>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>Должны вернуть арендаторам</div>
              </div>
              <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--red)' }}>
                {fmt(totalDepositsPaid)}
              </div>
            </div>
          </div>
        </div>

        {/* Loan balances */}
        {(data.loanRows && data.loanRows.length > 0) && (
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="section-title" style={{ marginBottom: 16 }}>Займы по контрагентам</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {/* Дебиторы */}
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--green)', marginBottom: 10 }}>
                  Нам должны (дебиторы) — {fmt(data.loanReceivable)}
                </div>
                {data.loanRows.filter(r => r.issued - r.repaid !== 0).map(r => (
                  <div key={'d-' + r.counterparty} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ fontSize: 13 }}>{r.counterparty}</span>
                    <span style={{ fontWeight: 600, color: (r.issued - r.repaid) >= 0 ? 'var(--green)' : 'var(--red)' }}>
                      {fmt(r.issued - r.repaid)}
                    </span>
                  </div>
                ))}
                {data.loanRows.every(r => r.issued - r.repaid === 0) && (
                  <div style={{ fontSize: 13, color: 'var(--text3)' }}>Нет задолженностей</div>
                )}
              </div>
              {/* Кредиторы */}
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--red)', marginBottom: 10 }}>
                  Мы должны (кредиторы) — {fmt(data.loanPayable)}
                </div>
                {data.loanRows.filter(r => r.received - r.returned !== 0).map(r => (
                  <div key={'k-' + r.counterparty} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ fontSize: 13 }}>{r.counterparty}</span>
                    <span style={{ fontWeight: 600, color: (r.received - r.returned) >= 0 ? 'var(--red)' : 'var(--green)' }}>
                      {fmt(r.received - r.returned)}
                    </span>
                  </div>
                ))}
                {data.loanRows.every(r => r.received - r.returned === 0) && (
                  <div style={{ fontSize: 13, color: 'var(--text3)' }}>Нет задолженностей</div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 14, paddingTop: 12, marginTop: 8, borderTop: '1px solid var(--border)' }}>
              <span>Нетто по займам</span>
              <span style={{ color: (data.loanReceivable - data.loanPayable) >= 0 ? 'var(--green)' : 'var(--red)' }}>
                {fmt(data.loanReceivable - data.loanPayable)}
              </span>
            </div>
          </div>
        )}

        {/* Deposits by contract */}
        <div className="card">
          <div className="section-title" style={{ marginBottom: 16 }}>
            Залоги по договорам — внесено {fmt(totalDepositsPaid)} / ожидается {fmt(totalDepositsExpected)}
          </div>
          {data.depositsByContract.length === 0 ? (
            <div style={{ color: 'var(--text3)', fontSize: 13 }}>Нет активных договоров с залогом</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Студия</th><th>Арендатор</th><th>Внесено</th><th>Ожидается</th></tr>
                </thead>
                <tbody>
                  {data.depositsByContract.filter(r => r.expected > 0 || r.paid > 0).map(r => {
                    const remaining = r.expected - r.paid;
                    return (
                      <tr key={r.id}>
                        <td><strong>{r.studio}</strong></td>
                        <td>{r.name}</td>
                        <td style={{ fontWeight: 600, color: r.paid > 0 ? 'var(--green)' : 'var(--text3)' }}>{fmt(r.paid)}</td>
                        <td style={{ color: remaining > 0 ? 'var(--yellow)' : 'var(--text3)', fontSize: 12 }}>
                          {fmt(r.expected)}{remaining > 0 && <span style={{ fontSize: 11, marginLeft: 4 }}>(-{fmt(remaining)})</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </>)}
    </div>
  );
}
